import React from 'react';
import ListenButton from './ListenButton';

// Safe highlight function that supports multi-word highlights without breaking sentence structure
function highlightByGrammar(sentence: string, highlights: string[] = []) {
    if (!highlights || highlights.length === 0) return sentence;

    // Sort highlights by length (longest first) to handle multi-word phrases
    const sortedHighlights = highlights.sort((a, b) => b.length - a.length);

    // Create a map of positions to highlight
    const highlightMap = new Map();

    // Process each highlight and mark positions
    sortedHighlights.forEach(highlight => {
        const cleanHighlight = highlight.toLowerCase().trim();
        const regex = new RegExp(`\\b${cleanHighlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');

        let match;
        while ((match = regex.exec(sentence)) !== null) {
            const start = match.index;
            const end = start + match[0].length;
            highlightMap.set(start, { end, text: match[0] });
        }
    });

    // Convert positions to sorted array
    const positions = Array.from(highlightMap.entries()).sort((a, b) => a[0] - b[0]);

    // Build the result with highlights
    const result = [];
    let lastIndex = 0;

    positions.forEach(([start, { end, text }]) => {
        // Add text before highlight
        if (start > lastIndex) {
            result.push(sentence.slice(lastIndex, start));
        }

        // Add highlighted text
        result.push(
            <span key={`highlight-${start}`} className="bg-yellow-200 font-bold px-1 rounded inline">
                {text}
            </span>
        );

        lastIndex = end;
    });

    // Add remaining text
    if (lastIndex < sentence.length) {
        result.push(sentence.slice(lastIndex));
    }

    // Wrap everything in a single span to ensure inline flow
    return (
        <span className="inline">
            {result.length > 0 ? result : sentence}
        </span>
    );
}

interface ItemCardProps {
    item: {
        sentence?: string;
        word?: string;
        en: string;
        hi: string;
        bn?: string;
        mr?: string;
        te?: string;
        ta?: string;
        gu?: string;
        meaning?: string;
        category?: string;
        [key: string]: string | undefined;
    };
    type: 'sentence' | 'word' | 'grammar';
    onDone: () => void;
    onMemorize: () => void;
    onFavorite: () => void;
    showMemorizeAgain?: boolean;
    showAddToFavorite?: boolean;
    selectedLanguage: string;
    isFavorite?: boolean;
    hideActions?: boolean; // new prop
    favoriteTooltip?: string;
}

export default function ItemCard({
    item,
    type,
    onDone,
    onMemorize,
    onFavorite,
    showMemorizeAgain = false,
    showAddToFavorite = true,
    selectedLanguage,
    isFavorite = false,
    hideActions = false, // default
    favoriteTooltip
}: ItemCardProps) {
    const displayText = type === 'sentence' ? item.sentence : item.word;
    const meaningText = type === 'word' ? item.meaning : item.category;

    // Get the translation for the selected language
    const getTranslation = (language: string) => {
        return item[language] || item.en;
    };

    // Show selected language prominently, English below if not English
    const selectedLanguageText = getTranslation(selectedLanguage);
    const englishText = selectedLanguage !== 'en' ? item.en : null;
    const highlight = Array.isArray(item.highlight) ? item.highlight : [];

    return (
        <div className="bg-white/60 backdrop-blur-2xl border border-blue-200 rounded-3xl p-4 sm:p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 flex flex-col items-center gap-4 sm:gap-6 relative overflow-hidden">
            <div className="mb-2 w-full text-center">
                {/* Selected language prominently displayed */}
                <div className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2 drop-shadow-lg tracking-tight">
                    {selectedLanguageText}
                </div>
                {/* English below in smaller text if not English */}
                {englishText && (
                    <div className="text-sm md:text-base text-gray-500 font-medium mb-2 flex items-center justify-center gap-2">
                        {highlight.length > 0 ? highlightByGrammar(englishText, highlight) : englishText}
                        <ListenButton text={item.en} />
                    </div>
                )}
                {meaningText && (
                    <div className="text-base text-purple-600 italic font-medium mb-2">
                        {meaningText}
                    </div>
                )}
            </div>

            {/* Only show button row if not hideActions */}
            {!hideActions && (
                <div className="flex flex-wrap gap-3 justify-center w-full">
                    {/* If only Unfavorite should be shown (Favorites section) */}
                    {showAddToFavorite && isFavorite ? (
                        <button
                            onClick={onFavorite}
                            className={`flex items-center gap-2 px-6 py-2 rounded-full shadow-lg font-bold text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 bg-yellow-500 text-white cursor-not-allowed`}
                            disabled={isFavorite}
                        >
                            <span>⭐</span> Unfavourite
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={onDone}
                                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-green-400 to-green-600 text-white rounded-full shadow-lg hover:from-green-500 hover:to-green-700 font-bold text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-300"
                            >
                                <span>✓</span> Done
                            </button>

                            {showMemorizeAgain ? (
                                <button
                                    onClick={onMemorize}
                                    className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-400 to-blue-600 text-white rounded-full shadow-lg hover:from-blue-500 hover:to-blue-700 font-bold text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                >
                                    <span>🔄</span> Memorize Again
                                </button>
                            ) : (
                                <button
                                    onClick={onMemorize}
                                    className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-400 to-purple-600 text-white rounded-full shadow-lg hover:from-purple-500 hover:to-purple-700 font-bold text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-300"
                                >
                                    <span>🧠</span> Memorize
                                </button>
                            )}

                            {showAddToFavorite && (
                                <button
                                    onClick={onFavorite}
                                    className={`flex items-center gap-2 px-6 py-2 rounded-full shadow-lg font-bold text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${isFavorite ? 'bg-yellow-500 text-white cursor-not-allowed' : 'bg-yellow-200 text-yellow-700 hover:bg-yellow-300'} ${isFavorite ? '' : 'hover:from-yellow-300 hover:to-yellow-400'}`}
                                    disabled={isFavorite}
                                    title={favoriteTooltip || (isFavorite ? 'Already favorited' : 'Favorite')}
                                >
                                    <span className={isFavorite ? 'text-yellow-600' : 'text-yellow-300'}>⭐</span> {isFavorite ? 'Favorited' : 'Favorite'}
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
            <span className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gradient-to-br from-blue-400 via-purple-400 to-pink-400 opacity-20 blur-2xl z-0"></span>
        </div>
    );
} 